import { OPTION_TYPE } from 'src/interfaces/Template.interface';

export type ImageMappingOptionType = OPTION_TYPE | 'IMAGE_FIELD';

export type ImageFieldRef = {
  id: number;
  api_name: string;
};

export type ImageMappingDestinationSlot = {
  id: number;
  api_name: string;
  api_code: string;
  api_id?: string;
  isRequired: boolean;
};

export type ImageMappingMeta = {
  sourceFieldId: number;
  destinationFieldId: number;
  /** Parsed from GET; used for Image Field dropdown size when type is supported */
  sourceImagesMaxCount: number | null;
  destinationSlots: ImageMappingDestinationSlot[];
  imageField: ImageFieldRef | null;
  supportsImageFieldMappingType: boolean;
};

export type ImageMappingRow = {
  id: number | null;
  position: number;
  destinationFieldDataId: number | null;
  attributeLabel: string;
  mappingOption: ImageMappingOptionType;
  mappedFieldId: number | null;
  valueStr: string;
};

export type ImageMappingIsDefaultPayloadKey = 'is_default' | 'isDefault';

/** Image template mapping API uses camelCase on GET/POST (`isDefault`). */
export const IMAGE_MAPPING_IS_DEFAULT_PAYLOAD_KEY: ImageMappingIsDefaultPayloadKey = 'isDefault';

export type ImageMappingFormState = {
  supported: boolean;
  meta: ImageMappingMeta | null;
  rows: ImageMappingRow[];
  imageTemplateMappingId: number | null;
  unavailableMessage: string | null;
  showImageMappingIsDefault: boolean;
  imageMappingIsDefault: boolean;
  /** POST key mirrored from GET (`is_default` preferred when both exist). */
  imageMappingIsDefaultPayloadKey: ImageMappingIsDefaultPayloadKey;
};

const IMAGE_FIELD_FIELD_TYPES = new Set(['IMAGE', 'IMAGE_FIELD']);

const parseSourceImagesMaxCount = (raw: unknown): number | null => {
  if (raw == null || raw === '') return null;
  const n = Number.parseInt(String(raw), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const parseImageFieldFromGet = (data: any): ImageFieldRef | null => {
  const field = data?.image_field;
  if (!field || typeof field !== 'object') return null;
  const id = field.id;
  const api_name = field.api_name ?? field.api_code;
  if (id == null || api_name == null || String(api_name).trim() === '') return null;
  const n = Number(id);
  if (!Number.isFinite(n)) return null;
  return { id: n, api_name: String(api_name) };
};

const collectDestinationSlots = (
  data: any
): { slots: ImageMappingDestinationSlot[]; destinationFieldId: number | null } => {
  const destField = data?.destination_image_field;
  let destinationFieldId: number | null =
    destField?.id != null
      ? Number(destField.id)
      : data?.destination_field_id != null
        ? Number(data.destination_field_id)
        : data?.destination_field != null
          ? Number(data.destination_field)
          : null;

  let rawSlots: any[] | undefined = destField?.platform_field_data;
  if (!rawSlots?.length && Array.isArray(data?.destination_images_data)) {
    rawSlots = data.destination_images_data;
  }

  const slots: ImageMappingDestinationSlot[] = (rawSlots ?? [])
    .filter((slot: any) => slot?.isActive !== false && slot?.isDeleted !== true)
    .map((slot: any) => ({
      id: Number(slot.id),
      api_name: slot.api_name ?? slot.api_code ?? `IMAGE_${slot.id}`,
      api_code: slot.api_code ?? slot.api_name ?? '',
      api_id: slot.api_id ?? slot.api_code ?? slot.api_name,
      isRequired: slot.is_required === true,
    }));

  if (!destinationFieldId && rawSlots?.[0]?.platform_field != null) {
    destinationFieldId = Number(rawSlots[0].platform_field);
  }

  return { slots, destinationFieldId };
};

const resolveSourceFieldId = (data: any): number | null => {
  const id =
    data?.source_image_field_data?.id ??
    data?.source_image_field?.id ??
    data?.source_field_id ??
    data?.source_field;
  return id != null ? Number(id) : null;
};

const unwrapImageMappingGetData = (payload: any) => {
  if (!payload) return null;
  if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
    return payload.data;
  }
  return payload;
};

const extractImageMappingTemplateId = (data: any): number | null => {
  const dict = data?.image_mapping_dict;
  if (dict && typeof dict === 'object' && dict.id != null) {
    return Number(dict.id);
  }
  if (data?.image_template_mapping_id != null && data?.image_template_mapping_id !== '') {
    return Number(data.image_template_mapping_id);
  }
  return null;
};

const readIsDefaultFlagFromRecord = (
  record: Record<string, unknown>
): boolean | null => {
  if (Object.prototype.hasOwnProperty.call(record, 'is_default')) {
    return Boolean(record.is_default);
  }
  if (Object.prototype.hasOwnProperty.call(record, 'isDefault')) {
    return Boolean(record.isDefault);
  }
  return null;
};

/** Reads template default flag from `image_mapping_dict` (snake or camel value). POST always uses `isDefault`. */
export const resolveImageMappingIsDefaultFromDict = (
  dict: unknown
): { value: boolean; payloadKey: ImageMappingIsDefaultPayloadKey } => {
  if (!dict || typeof dict !== 'object') {
    return { value: false, payloadKey: IMAGE_MAPPING_IS_DEFAULT_PAYLOAD_KEY };
  }
  const value = readIsDefaultFlagFromRecord(dict as Record<string, unknown>);
  return {
    value: value ?? false,
    payloadKey: IMAGE_MAPPING_IS_DEFAULT_PAYLOAD_KEY,
  };
};

/** Resolves default flag from GET `data` (`image_mapping_dict`, then root template fields). */
export const resolveImageMappingIsDefaultFromGetData = (
  data: unknown
): { value: boolean; payloadKey: ImageMappingIsDefaultPayloadKey } => {
  if (!data || typeof data !== 'object') {
    return { value: false, payloadKey: IMAGE_MAPPING_IS_DEFAULT_PAYLOAD_KEY };
  }
  const record = data as Record<string, unknown>;
  const dict = record.image_mapping_dict;
  if (dict && typeof dict === 'object') {
    const fromDict = readIsDefaultFlagFromRecord(dict as Record<string, unknown>);
    if (fromDict != null) {
      return { value: fromDict, payloadKey: IMAGE_MAPPING_IS_DEFAULT_PAYLOAD_KEY };
    }
  }
  const fromRoot = readIsDefaultFlagFromRecord(record);
  return {
    value: fromRoot ?? false,
    payloadKey: IMAGE_MAPPING_IS_DEFAULT_PAYLOAD_KEY,
  };
};

/** Saved row lines from GET (`platform_image_mapping_data` in ticket / backend). */
export const extractPlatformImageMappingDataFromGet = (data: any): any[] | null => {
  const dict = data?.image_mapping_dict;
  if (dict && typeof dict === 'object') {
    const nested =
      dict.platform_image_mapping_data ??
      dict.image_mapping_template_data ??
      dict.image_mapping_data;
    if (Array.isArray(nested)) {
      return nested;
    }
  }
  if (Array.isArray(data?.platform_image_mapping_data)) {
    return data.platform_image_mapping_data;
  }
  return null;
};

export const defaultSourceImageShortcode = (position: number) => `[IMAGE_${position}]`;

export const buildImageMappingSourceImageOptionLabels = (meta: ImageMappingMeta): string[] => {
  if (!meta.supportsImageFieldMappingType || !meta.imageField || meta.sourceImagesMaxCount == null) {
    return [];
  }
  const { api_name } = meta.imageField;
  return Array.from(
    { length: meta.sourceImagesMaxCount },
    (_, index) => `${api_name}-${index + 1}`
  );
};

export const isValidImageFieldSourceIndex = (
  index: number,
  meta: ImageMappingMeta
): boolean =>
  meta.supportsImageFieldMappingType &&
  meta.sourceImagesMaxCount != null &&
  Number.isFinite(index) &&
  index >= 1 &&
  index <= meta.sourceImagesMaxCount;

export const buildDefaultImageMappingRows = (meta: ImageMappingMeta): ImageMappingRow[] =>
  meta.destinationSlots.map((slot, index) => ({
    id: null,
    position: index + 1,
    destinationFieldDataId: slot.id,
    attributeLabel: slot.api_code || slot.api_id || slot.api_name,
    mappingOption: 'DEFAULT',
    mappedFieldId: null,
    valueStr: '',
  }));

const idFromMappedFieldApiRef = (ref: unknown): number | null => {
  if (ref == null || ref === '') return null;
  if (typeof ref === 'object') {
    const id = (ref as { id?: unknown }).id;
    if (id == null || id === '') return null;
    const n = Number(id);
    return Number.isFinite(n) ? n : null;
  }
  const n = Number(ref);
  return Number.isFinite(n) ? n : null;
};

const mappingOptionFromMappedFieldObject = (
  field: Record<string, unknown>
): ImageMappingOptionType | null => {
  const fieldType = field.field_type ?? field.fieldType;
  if (fieldType === 'CUSTOM') return 'CUSTOM';
  if (fieldType === 'NATIVE' || fieldType === 'LOCAL') return 'NATIVE';
  if (typeof fieldType === 'string' && IMAGE_FIELD_FIELD_TYPES.has(fieldType)) {
    return 'IMAGE_FIELD';
  }
  return null;
};

const resolveMappedFieldIdFromSavedRow = (row: any): number | null =>
  idFromMappedFieldApiRef(row.mapped_field) ?? idFromMappedFieldApiRef(row.mapped_field_id);

const resolveMappingOptionFromSavedRow = (row: any, meta: ImageMappingMeta): ImageMappingOptionType => {
  const mappedRef = row.mapped_field ?? row.mapped_field_id ?? null;
  if (mappedRef != null && mappedRef !== '') {
    if (typeof mappedRef === 'object') {
      const fromObject = mappingOptionFromMappedFieldObject(mappedRef as Record<string, unknown>);
      if (fromObject) return fromObject;
    }
    const mappedId = resolveMappedFieldIdFromSavedRow(row);
    const savedPosition = Number(row.position);
    if (
      mappedId != null &&
      meta.supportsImageFieldMappingType &&
      meta.imageField?.id === mappedId &&
      isValidImageFieldSourceIndex(savedPosition, meta) &&
      (row.value_str == null || row.value_str === '')
    ) {
      return 'IMAGE_FIELD';
    }
    if (mappedId != null) {
      return 'NATIVE';
    }
  }
  return 'DEFAULT';
};

const resolveRowPositionFromSaved = (
  saved: any,
  mappingOption: ImageMappingOptionType,
  slotIndex: number,
  meta: ImageMappingMeta
): number => {
  const savedPosition = Number(saved.position);
  if (mappingOption === 'IMAGE_FIELD' && isValidImageFieldSourceIndex(savedPosition, meta)) {
    return savedPosition;
  }
  if (Number.isFinite(savedPosition) && savedPosition > 0) {
    return savedPosition;
  }
  return slotIndex + 1;
};

const buildRowFromSavedSlot = (
  meta: ImageMappingMeta,
  slot: ImageMappingDestinationSlot,
  slotIndex: number,
  saved: any
): ImageMappingRow => {
  const mappingOption = resolveMappingOptionFromSavedRow(saved, meta);
  const mappedFieldId = resolveMappedFieldIdFromSavedRow(saved);
  const position = resolveRowPositionFromSaved(saved, mappingOption, slotIndex, meta);
  return {
    id: saved.id ?? null,
    position,
    destinationFieldDataId: slot.id,
    attributeLabel: slot.api_code || slot.api_id || slot.api_name,
    mappingOption,
    mappedFieldId:
      mappingOption === 'IMAGE_FIELD' ? meta.imageField?.id ?? mappedFieldId : mappedFieldId,
    valueStr:
      mappingOption === 'DEFAULT'
        ? saved.value_str ?? ''
        : '',
  };
};

const mergeSavedRowsWithSlots = (meta: ImageMappingMeta, savedRows: any[]): ImageMappingRow[] => {
  const slotById = new Map(meta.destinationSlots.map((slot) => [slot.id, slot]));
  const rows: ImageMappingRow[] = [];

  savedRows.forEach((saved) => {
    const destId =
      saved.destination_field_data?.id ??
      saved.destination_field_data_id ??
      saved.destination_field_data;
    if (destId == null) return;
    const slot = slotById.get(Number(destId));
    if (!slot) return;
    const slotIndex = meta.destinationSlots.findIndex((s) => s.id === slot.id);
    rows.push(
      buildRowFromSavedSlot(meta, slot, slotIndex >= 0 ? slotIndex : rows.length, saved)
    );
  });

  return rows;
};

export const listImageMappingSlotsAvailableToAdd = (
  meta: ImageMappingMeta,
  rows: ImageMappingRow[]
): ImageMappingDestinationSlot[] => {
  const usedDestIds = new Set(
    rows.map((row) => row.destinationFieldDataId).filter((id): id is number => id != null)
  );
  return meta.destinationSlots.filter((slot) => !usedDestIds.has(slot.id));
};

export const buildImageMappingRowForSlot = (
  meta: ImageMappingMeta,
  slot: ImageMappingDestinationSlot,
  existingRows: ImageMappingRow[]
): ImageMappingRow => {
  const slotIndex = meta.destinationSlots.findIndex((s) => s.id === slot.id);
  const position =
    existingRows.length === 0
      ? (slotIndex >= 0 ? slotIndex : 0) + 1
      : Math.max(...existingRows.map((row) => row.position), 0) + 1;
  return {
    id: null,
    position,
    destinationFieldDataId: slot.id,
    attributeLabel: slot.api_code || slot.api_id || slot.api_name,
    mappingOption: 'DEFAULT',
    mappedFieldId: null,
    valueStr: '',
  };
};

export const rowsFromSavedImageMappingData = (
  meta: ImageMappingMeta,
  savedRows: any[] | undefined | null,
  options?: { hasPersistedImageTemplate?: boolean }
): ImageMappingRow[] => {
  if (savedRows == null) {
    return buildDefaultImageMappingRows(meta);
  }
  if (savedRows.length === 0) {
    return options?.hasPersistedImageTemplate ? [] : buildDefaultImageMappingRows(meta);
  }
  return mergeSavedRowsWithSlots(meta, savedRows);
};

export const applyImageMappingOptionChange = (
  row: ImageMappingRow,
  option: ImageMappingOptionType,
  meta: ImageMappingMeta,
  destinationSlotIndex: number
): ImageMappingRow => {
  const destinationPosition = destinationSlotIndex >= 0 ? destinationSlotIndex + 1 : row.position;

  if (option === 'DEFAULT') {
    return {
      ...row,
      mappingOption: option,
      mappedFieldId: null,
      position: destinationPosition,
      valueStr: row.valueStr ?? '',
    };
  }

  if (option === 'IMAGE_FIELD') {
    return {
      ...row,
      mappingOption: option,
      valueStr: '',
      mappedFieldId: meta.imageField?.id ?? null,
      position: 0,
    };
  }

  return {
    ...row,
    mappingOption: option,
    valueStr: '',
    mappedFieldId: null,
    position: destinationPosition,
  };
};

export const isImageMappingRowComplete = (
  row: ImageMappingRow,
  meta: ImageMappingMeta
): boolean => {
  if (row.mappingOption === 'IMAGE_FIELD') {
    return (
      meta.supportsImageFieldMappingType &&
      meta.imageField != null &&
      row.mappedFieldId === meta.imageField.id &&
      isValidImageFieldSourceIndex(row.position, meta)
    );
  }
  if (row.mappingOption === 'NATIVE' || row.mappingOption === 'CUSTOM') {
    return row.mappedFieldId != null;
  }
  return Boolean(row.valueStr?.trim());
};

export const getMissingRequiredImageMappingSlotLabels = (
  meta: ImageMappingMeta,
  rows: ImageMappingRow[]
): string[] => {
  const missing: string[] = [];
  const rowByDestId = new Map(
    rows
      .filter((row) => row.destinationFieldDataId != null)
      .map((row) => [row.destinationFieldDataId as number, row])
  );

  meta.destinationSlots.forEach((slot) => {
    if (!slot.isRequired) return;
    const label = slot.api_code || slot.api_name;
    const row = rowByDestId.get(slot.id);
    if (!row || !isImageMappingRowComplete(row, meta)) {
      missing.push(label);
    }
  });

  return missing;
};

export const applyImageMappingGetResponse = (payload: any): ImageMappingFormState => {
  const data = unwrapImageMappingGetData(payload);
  if (!data) {
    return {
      supported: false,
      meta: null,
      rows: [],
      imageTemplateMappingId: null,
      unavailableMessage: 'Image mapping data could not be loaded.',
      showImageMappingIsDefault: false,
      imageMappingIsDefault: false,
      imageMappingIsDefaultPayloadKey: IMAGE_MAPPING_IS_DEFAULT_PAYLOAD_KEY,
    };
  }

  const { slots, destinationFieldId } = collectDestinationSlots(data);
  const sourceFieldId = resolveSourceFieldId(data);

  if (!sourceFieldId) {
    return {
      supported: false,
      meta: null,
      rows: [],
      imageTemplateMappingId: null,
      unavailableMessage:
        "Image mapping isn't available for this marketplace yet, but it's coming soon. Your images will be sent as-is from your source system. If you need to reorder images or map them from custom fields, contact your account manager and we'll help set it up.",
      showImageMappingIsDefault: false,
      imageMappingIsDefault: false,
      imageMappingIsDefaultPayloadKey: IMAGE_MAPPING_IS_DEFAULT_PAYLOAD_KEY,
    };
  }

  if (!destinationFieldId || slots.length === 0) {
    return {
      supported: false,
      meta: null,
      rows: [],
      imageTemplateMappingId: null,
      unavailableMessage:
        "Image mapping isn't available for this marketplace yet, but it's coming soon. Your images will be sent as-is from your source system. If you need to reorder images or map them from custom fields, contact your account manager and we'll help set it up.",
      showImageMappingIsDefault: false,
      imageMappingIsDefault: false,
      imageMappingIsDefaultPayloadKey: IMAGE_MAPPING_IS_DEFAULT_PAYLOAD_KEY,
    };
  }

  const sourceImagesMaxCount = parseSourceImagesMaxCount(data?.source_images_max_count);
  const imageField = parseImageFieldFromGet(data);
  const supportsImageFieldMappingType =
    imageField != null && sourceImagesMaxCount != null;

  const meta: ImageMappingMeta = {
    sourceFieldId,
    destinationFieldId,
    sourceImagesMaxCount,
    destinationSlots: slots,
    imageField,
    supportsImageFieldMappingType,
  };

  const imageTemplateMappingId = extractImageMappingTemplateId(data);
  const savedRowsFromGet = extractPlatformImageMappingDataFromGet(data);
  const isDefaultConfig = resolveImageMappingIsDefaultFromGetData(data);

  return {
    supported: true,
    meta,
    rows: rowsFromSavedImageMappingData(meta, savedRowsFromGet, {
      hasPersistedImageTemplate: imageTemplateMappingId != null,
    }),
    imageTemplateMappingId,
    unavailableMessage: null,
    showImageMappingIsDefault: true,
    imageMappingIsDefault: isDefaultConfig.value,
    imageMappingIsDefaultPayloadKey: isDefaultConfig.payloadKey,
  };
};

export const hasImageMappingUserInput = (rows: ImageMappingRow[], meta: ImageMappingMeta): boolean => {
  if (!rows.length) return false;
  return rows.some((row) => isImageMappingRowComplete(row, meta));
};

export type ImageMappingSaveRowPayload = {
  id: number | null;
  destination_field_data_id: number | null;
  mapped_field_id: number | null;
  position: number;
  value_str: string;
};

export type ImageMappingSavePayload = {
  image_template_mapping_id: number | null;
  destination_platform_id: number;
  source_field_id: number;
  destination_field_id: number;
  name: string;
  is_active: boolean;
  template_mapping_id: number;
  image_mapping_data: ImageMappingSaveRowPayload[];
  is_default?: boolean;
  isDefault?: boolean;
};

export const buildImageMappingSavePayload = (input: {
  imageTemplateMappingId: number | null;
  destinationPlatformId: number;
  templateMappingId: number;
  ruleName: string;
  meta: ImageMappingMeta;
  rows: ImageMappingRow[];
  isActive?: boolean;
  includeIsDefault?: boolean;
  isDefault?: boolean;
  isDefaultPayloadKey?: ImageMappingIsDefaultPayloadKey;
}): ImageMappingSavePayload => {
  const rows = input.rows.filter(
    (row) => row.destinationFieldDataId != null && isImageMappingRowComplete(row, input.meta)
  );

  const payload: ImageMappingSavePayload = {
    image_template_mapping_id: input.imageTemplateMappingId,
    destination_platform_id: input.destinationPlatformId,
    source_field_id: input.meta.sourceFieldId,
    destination_field_id: input.meta.destinationFieldId,
    name: input.ruleName.trim(),
    is_active: input.isActive ?? true,
    template_mapping_id: input.templateMappingId,
    image_mapping_data: rows.map((row) => {
      const isDefaultMapping = row.mappingOption === 'DEFAULT';
      return {
        id: row.id,
        destination_field_data_id: row.destinationFieldDataId,
        mapped_field_id: isDefaultMapping ? null : row.mappedFieldId,
        position: row.position,
        value_str: isDefaultMapping ? row.valueStr?.trim() ?? '' : '',
      };
    }),
  };

  if (input.includeIsDefault) {
    payload.isDefault = input.isDefault ?? false;
  }

  return payload;
};

export const extractCreatedRecipeId = (payload: any): number | null => {
  if (!payload) return null;
  const data = payload.data ?? payload;
  if (Array.isArray(data)) {
    return data[0]?.id ?? null;
  }
  return data?.id ?? payload?.id ?? null;
};

export const extractImageTemplateMappingId = (payload: any): number | null => {
  const data = unwrapImageMappingGetData(payload);
  if (!data) return null;
  return extractImageMappingTemplateId(data) ?? (data?.id != null ? Number(data.id) : null);
};

export const isImageMappingSlotRequired = (
  meta: ImageMappingMeta,
  destinationFieldDataId: number | null | undefined
): boolean => {
  if (destinationFieldDataId == null) return false;
  return (
    meta.destinationSlots.find((slot) => slot.id === destinationFieldDataId)?.isRequired === true
  );
};
